import { Command } from 'commander';
import chalk from 'chalk';
import { join } from 'path';
import { logger } from '../lib/logger.js';
import { installCommands, installDefaultPlugins, getDefaultPlugins, setTemplatesDir, getTemplatesDir, detectDevTemplates } from '../lib/installer.js';
import { detectProject } from '../lib/detector.js';

export const installCommand = new Command('install')
  .alias('i')
  .description('Install SmartStack commands, agents, and hooks')
  .option('-f, --force', 'Overwrite existing files')
  .option('-g, --global', 'Install to user directory ~/.claude (default)', true)
  .option('-l, --local', 'Install to project directory ./.claude')
  .option('--commands-only', 'Install only commands')
  .option('--agents-only', 'Install only agents')
  .option('--hooks-only', 'Install only hooks')
  .option('--no-config', 'Skip config file creation')
  .option('--no-plugins', 'Skip Claude Code plugin installation')
  .option('--dev [path]', 'Install from local dev directory (default: auto-detect SmartStack.cli repo)')
  .action(async (options) => {
    logger.header('Installing SmartStack Commands');

    // Handle --dev flag: override templates source
    if (options.dev !== undefined) {
      let devPath: string;

      if (typeof options.dev === 'string' && options.dev !== '') {
        // Explicit path provided
        devPath = options.dev;
      } else {
        // Auto-detect: look for SmartStack.cli in common locations
        const candidates = [
          join(process.cwd()),
          join(process.cwd(), '..', 'SmartStack.cli', '02-Develop'),
          join(process.cwd(), '..', '..', 'SmartStack.cli', '02-Develop'),
        ];
        const found = candidates.find(c => detectDevTemplates(c) !== null);
        if (!found) {
          logger.error('Cannot auto-detect SmartStack.cli dev directory.');
          logger.error('Provide the path explicitly: ss install --dev /path/to/SmartStack.cli/02-Develop');
          process.exit(1);
        }
        devPath = found;
      }

      const devTemplates = detectDevTemplates(devPath);
      if (!devTemplates) {
        logger.error(`Not a valid SmartStack CLI dev directory: ${devPath}`);
        logger.error('Expected: package.json with name "@atlashub/smartstack-cli" + templates/skills/');
        process.exit(1);
      }

      setTemplatesDir(devTemplates);
      logger.info(chalk.yellow(`DEV MODE: Reading templates from ${devTemplates}`));
      console.log();
    }

    const isGlobal = !options.local;

    const spinner = logger.spinner('Analyzing environment...');
    const project = await detectProject();
    spinner.stop();

    console.log();
    logger.info(`Install location: ${isGlobal ? chalk.cyan('~/.claude (global)') : chalk.yellow('./.claude (local)')}`);
    logger.info(`Templates source: ${chalk.cyan(getTemplatesDir())}`);
    logger.info(`Git repository: ${project.isGitRepo ? chalk.green('Yes') : chalk.gray('No')}`);
    logger.info(`.NET project: ${project.hasDotNet ? chalk.green('Yes') : chalk.gray('No')}`);
    logger.info(`EF Core: ${project.hasEfCore ? chalk.green('Yes') : chalk.gray('No')}`);
    console.log();

    const components: ('commands' | 'agents' | 'hooks' | 'skills' | 'all')[] = [];
    if (options.commandsOnly) components.push('commands');
    else if (options.agentsOnly) components.push('agents');
    else if (options.hooksOnly) components.push('hooks');
    else components.push('all');

    try {
      const result = await installCommands({
        force: options.force,
        global: isGlobal,
        skipConfig: !options.config,
        components,
      });

      console.log();

      if (result.errors.length > 0) {
        logger.warning(`Completed with ${result.errors.length} error(s)`);
      }

      // Install Claude Code plugins (unless --no-plugins is specified)
      let pluginSummary: string[] = [];
      if (options.plugins !== false) {
        console.log();
        logger.info('Installing Claude Code plugins...');

        const pluginResult = await installDefaultPlugins({ force: options.force });

        if (pluginResult.installed.length > 0 || pluginResult.skipped.length > 0) {
          pluginSummary = [
            '',
            chalk.bold('Plugins:'),
          ];

          for (const name of pluginResult.installed) {
            pluginSummary.push(`  ${chalk.green('✓')} ${chalk.cyan(name)} - installed`);
          }
          for (const name of pluginResult.skipped) {
            pluginSummary.push(`  ${chalk.yellow('○')} ${chalk.gray(name)} - already installed`);
          }
          for (const { name, error } of pluginResult.failed) {
            pluginSummary.push(`  ${chalk.red('✗')} ${chalk.gray(name)} - ${error}`);
          }
        }
      } else {
        const defaultPlugins = getDefaultPlugins();
        if (defaultPlugins.length > 0) {
          pluginSummary = [
            '',
            chalk.bold('Plugins (skipped):'),
            `  ${chalk.gray('Use without --no-plugins to install:')}`,
          ];
          for (const plugin of defaultPlugins) {
            pluginSummary.push(`  ${chalk.gray(`  - ${plugin.name}`)}`);
          }
        }
      }

      const summary = [
        chalk.green.bold('Installation complete!'),
        '',
        `Files installed: ${chalk.cyan(result.installed)}`,
        `Files skipped: ${chalk.yellow(result.skipped)} (use --force to overwrite)`,
        '',
        'Available skills in Claude Code:',
        '',
        chalk.bold('GitFlow:'),
        `  ${chalk.cyan('/gitflow')}           - Full GitFlow workflow (CLI-driven)`,
        '',
        chalk.bold('Business Analysis:'),
        `  ${chalk.cyan('/ba-create-prd')}     - Synthesise the BA tree into a PRD`,
        `  ${chalk.cyan('/ba-audit-prd')}      - PRD quality audit (GO/NO-GO gate)`,
        '',
        chalk.bold('EF Core:'),
        `  ${chalk.cyan('/efcore')}             - Migration orchestrator`,
        '',
        chalk.bold('Development:'),
        `  ${chalk.cyan('/ba-develop')}        - PRD-driven phased development`,
        ...pluginSummary,
      ];

      logger.box(summary, 'success');
    } catch (error) {
      logger.error(error instanceof Error ? error.message : 'Installation failed');
      process.exit(1);
    }
  });
